/**
 * Voice assistant controller hook (V3-PROVIDER foundation).
 * Orchestrates capture, command queue, sessions, pending confirmation, and cleanup.
 * Does not replace legacy hooks until mount points migrate.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { VoiceCommandRequest, VoiceCommandResponse, VoicePendingAction } from '../voice/types';
import {
  VoiceCommandQueue,
  shouldApplyCommandResponse,
  decideResponseSideEffects,
  type EnqueueResult,
} from '../voice/commandQueue';
import { createVoiceCommandId, rotateVoiceUiSessionId } from '../utils/voiceCommandClient';
import type { VoiceMessage } from '../utils/voiceSessionStorage';

const makeMessage = (role: 'user' | 'assistant', content: string): VoiceMessage => ({
  id: createVoiceCommandId(),
  role,
  content,
  timestamp: new Date().toISOString(),
});

export type VoiceCapturePhase = 'idle' | 'capturing' | 'finalizing';

export type VoiceAssistantResetReason =
  | 'logout'
  | 'business-switch'
  | 'new-chat'
  | 'unmount';

export interface VoiceAssistantControllerState {
  generation: number;
  uiSessionId: string;
  backendSessionId: string | null;
  captureId: string | null;
  capturePhase: VoiceCapturePhase;
  activeCommandId: string | null;
  commandState: 'idle' | 'queued' | 'processing' | 'awaiting-confirmation' | 'completed' | 'error';
  pendingAction: VoicePendingAction | null;
  messages: VoiceMessage[];
  isSpeaking: boolean;
  isConfirmSubmitting: boolean;
}

export interface VoiceAssistantControllerActions {
  startCapture: () => string;
  doneCapture: () => void;
  cancelCapture: () => void;
  submitText: (text: string, options?: Partial<VoiceCommandRequest>) => Promise<VoiceCommandResponse | null>;
  confirmPending: () => Promise<VoiceCommandResponse | null>;
  denyPending: () => Promise<VoiceCommandResponse | null>;
  stopSpeaking: () => void;
  reset: (reason: VoiceAssistantResetReason) => void;
}

export type UseVoiceAssistantControllerOptions = {
  sendCommand?: (request: VoiceCommandRequest, signal: AbortSignal) => Promise<VoiceCommandResponse>;
  onNavigate?: (route: string) => void;
  onSpeak?: (text: string) => void;
  onStopSpeak?: () => void;
  isMutationIntent?: (text: string) => boolean;
};

const defaultSendCommand = async (): Promise<VoiceCommandResponse> => ({
  success: true,
  message: '',
  command_id: createVoiceCommandId(),
  session_id: createVoiceCommandId(),
  phase: 'completed',
});

export function useVoiceAssistantController(
  options: UseVoiceAssistantControllerOptions = {}
): VoiceAssistantControllerState & VoiceAssistantControllerActions {
  const sendCommand = options.sendCommand ?? defaultSendCommand;
  const isMutationIntent = options.isMutationIntent ?? (() => false);

  const queueRef = useRef(new VoiceCommandQueue());
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const generationRef = useRef(0);
  const uiSessionIdRef = useRef(rotateVoiceUiSessionId());
  const backendSessionIdRef = useRef<string | null>(null);
  const captureIdRef = useRef<string | null>(null);
  const activeCommandIdRef = useRef<string | null>(null);
  const confirmSubmittingRef = useRef(false);
  const abortControllersRef = useRef<Set<AbortController>>(new Set());
  const globalListenersRegisteredRef = useRef(false);

  const [generation, setGeneration] = useState(0);
  const [capturePhase, setCapturePhase] = useState<VoiceCapturePhase>('idle');
  const [commandState, setCommandState] = useState<VoiceAssistantControllerState['commandState']>('idle');
  const [pendingAction, setPendingAction] = useState<VoicePendingAction | null>(null);
  const [messages, setMessages] = useState<VoiceMessage[]>([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isConfirmSubmitting, setIsConfirmSubmitting] = useState(false);

  const bumpGeneration = useCallback(() => {
    generationRef.current += 1;
    setGeneration(generationRef.current);
  }, []);

  const abortInFlight = useCallback(() => {
    for (const controller of abortControllersRef.current) {
      controller.abort();
    }
    abortControllersRef.current.clear();
    queueRef.current.reset();
  }, []);

  const reset = useCallback(
    (reason: VoiceAssistantResetReason) => {
      abortInFlight();
      bumpGeneration();
      uiSessionIdRef.current = rotateVoiceUiSessionId();
      backendSessionIdRef.current = null;
      captureIdRef.current = null;
      activeCommandIdRef.current = null;
      confirmSubmittingRef.current = false;
      setCapturePhase('idle');
      setCommandState('idle');
      setPendingAction(null);
      setIsSpeaking(false);
      setIsConfirmSubmitting(false);
      if (reason === 'new-chat') {
        setMessages([]);
      }
      optionsRef.current.onStopSpeak?.();
    },
    [abortInFlight, bumpGeneration]
  );

  const registerGlobalListenersOnce = useCallback(() => {
    if (globalListenersRegisteredRef.current || typeof window === 'undefined') return;
    globalListenersRegisteredRef.current = true;

    const onBusinessSwitch = () => reset('business-switch');
    const onLogout = () => reset('logout');

    window.addEventListener('voiceBusinessSwitch', onBusinessSwitch);
    window.addEventListener('voiceLogout', onLogout);
  }, [reset]);

  useEffect(() => {
    registerGlobalListenersOnce();
    return () => {
      reset('unmount');
    };
  }, [registerGlobalListenersOnce, reset]);

  const startCapture = useCallback((): string => {
    const captureId = createVoiceCommandId();
    captureIdRef.current = captureId;
    setCapturePhase('capturing');
    return captureId;
  }, []);

  const doneCapture = useCallback(() => {
    setCapturePhase('finalizing');
  }, []);

  const cancelCapture = useCallback(() => {
    captureIdRef.current = null;
    setCapturePhase('idle');
  }, []);

  const stopSpeaking = useCallback(() => {
    setIsSpeaking(false);
    optionsRef.current.onStopSpeak?.();
  }, []);

  const dispatchCommand = useCallback(
    async (
      voiceText: string,
      partial: Partial<VoiceCommandRequest> = {}
    ): Promise<VoiceCommandResponse | null> => {
      const commandId = partial.command_id ?? createVoiceCommandId();
      const mutation = isMutationIntent(voiceText);
      const enqueueResult: EnqueueResult = queueRef.current.enqueue(commandId, mutation);
      if (!enqueueResult.accepted) {
        setCommandState('idle');
        return null;
      }

      if (!queueRef.current.markActive(commandId)) {
        queueRef.current.complete(commandId);
        return null;
      }

      activeCommandIdRef.current = commandId;
      setCommandState('processing');

      const controller = new AbortController();
      abortControllersRef.current.add(controller);

      const request: VoiceCommandRequest = {
        command_id: commandId,
        capture_id: captureIdRef.current ?? undefined,
        ui_session_id: uiSessionIdRef.current,
        session_id: backendSessionIdRef.current ?? undefined,
        source: partial.source ?? 'text',
        voice_text: voiceText,
        current_page: partial.current_page,
        page_context: partial.page_context,
        client_state: partial.client_state,
        pending_command_id: partial.pending_command_id,
        confirmed: partial.confirmed,
        created_at: Date.now(),
      };

      try {
        const response = await optionsRef.current.sendCommand?.(
          request,
          enqueueResult.entry.abortController.signal
        ) ?? (await sendCommand(request, enqueueResult.entry.abortController.signal));

        if (
          !shouldApplyCommandResponse(
            request,
            uiSessionIdRef.current,
            activeCommandIdRef.current
          )
        ) {
          return null;
        }

        if (response.session_id) {
          backendSessionIdRef.current = response.session_id;
        }

        if (response.pending_action) {
          setPendingAction(response.pending_action);
          setCommandState('awaiting-confirmation');
        } else {
          setPendingAction(null);
          setCommandState('completed');
        }

        setMessages((prev) => [
          ...prev,
          makeMessage('user', voiceText),
          makeMessage('assistant', response.message),
        ]);

        const effects = decideResponseSideEffects(response, request, uiSessionIdRef.current);
        if (effects.speak && response.message) {
          setIsSpeaking(true);
          optionsRef.current.onSpeak?.(response.message);
        }
        if (effects.navigate && typeof response.data === 'object' && response.data && 'route' in response.data) {
          optionsRef.current.onNavigate?.(String((response.data as { route: string }).route));
        }

        return response;
      } catch {
        setCommandState('error');
        return null;
      } finally {
        abortControllersRef.current.delete(controller);
        queueRef.current.complete(commandId);
        activeCommandIdRef.current = null;
      }
    },
    [isMutationIntent]
  );

  const submitText = useCallback(
    (text: string, partial?: Partial<VoiceCommandRequest>) => dispatchCommand(text, partial),
    [dispatchCommand]
  );

  const confirmPending = useCallback(async (): Promise<VoiceCommandResponse | null> => {
    if (confirmSubmittingRef.current || !pendingAction?.command_id) return null;
    confirmSubmittingRef.current = true;
    setIsConfirmSubmitting(true);
    try {
      return await dispatchCommand('yes confirm', {
        source: 'confirmation',
        pending_command_id: pendingAction.command_id,
        confirmed: true,
      });
    } finally {
      confirmSubmittingRef.current = false;
      setIsConfirmSubmitting(false);
    }
  }, [dispatchCommand, pendingAction]);

  const denyPending = useCallback(async (): Promise<VoiceCommandResponse | null> => {
    if (!pendingAction?.command_id) return null;
    return dispatchCommand('cancel', {
      source: 'confirmation',
      pending_command_id: pendingAction.command_id,
      confirmed: false,
    });
  }, [dispatchCommand, pendingAction]);

  const state = useMemo(
    (): VoiceAssistantControllerState => ({
      generation,
      uiSessionId: uiSessionIdRef.current,
      backendSessionId: backendSessionIdRef.current,
      captureId: captureIdRef.current,
      capturePhase,
      activeCommandId: activeCommandIdRef.current,
      commandState,
      pendingAction,
      messages,
      isSpeaking,
      isConfirmSubmitting,
    }),
    [
      generation,
      capturePhase,
      commandState,
      pendingAction,
      messages,
      isSpeaking,
      isConfirmSubmitting,
    ]
  );

  return {
    ...state,
    startCapture,
    doneCapture,
    cancelCapture,
    submitText,
    confirmPending,
    denyPending,
    stopSpeaking,
    reset,
  };
}
